import { Box, Chip, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { DragIndicator, Visibility, VisibilityOff } from "@mui/icons-material";
import TasksCard from "./TasksCard";
import SubmissoinsCard from "./SubmissionsCard";
import PassportExpiryWidget from "./PassportExpiryWidget";
import RemindersWidget from "./RemindersWidget";

const CARD_HEIGHT = 280;

const CARD_LABELS: Record<string, string> = {
  tasks: "Recent Tasks",
  reminders: "Reminders",
  submissions: "Recent Submissions",
  passport: "Passport Alerts",
};

const CARD_COMPONENTS: Record<string, React.ReactNode> = {
  tasks: <TasksCard />,
  reminders: <RemindersWidget />,
  submissions: <SubmissoinsCard />,
  passport: <PassportExpiryWidget />,
};

interface Props {
  cardOrder: string[];
  hiddenCards: Set<string>;
  editMode: boolean;
  onReorder: (next: string[]) => void;
  onToggleCard: (id: string) => void;
}

function RecentSection({ cardOrder, hiddenCards, editMode, onReorder, onToggleCard }: Props) {
  const [dragOver, setDragOver] = useState<string | null>(null);
  const dragging = useRef<string | null>(null);

  const handleDrop = (target: string) => {
    setDragOver(null);
    const from = dragging.current;
    if (!from || from === target) return;
    const next = [...cardOrder];
    const fi = next.indexOf(from);
    const ti = next.indexOf(target);
    next.splice(fi, 1);
    next.splice(ti, 0, from);
    onReorder(next);
    dragging.current = null;
  };

  const visibleCards = cardOrder.filter((id) => !hiddenCards.has(id));
  const colCount = visibleCards.length || 1;

  return (
    <Grid container spacing={2}>
      {cardOrder.map((id) => {
        const isHidden = hiddenCards.has(id);

        // Hidden + not editing → render nothing
        if (isHidden && !editMode) return null;

        // Hidden + editing → compact placeholder
        if (isHidden && editMode) {
          return (
            <Grid key={id} item xs={12} sm={6} lg={3}>
              <Box
                sx={{
                  height: 52,
                  borderRadius: "8px",
                  border: "2px dashed #E2E8F0",
                  bgcolor: "#F8FAFC",
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <VisibilityOff sx={{ fontSize: 15, color: "#CBD5E1" }} />
                  <Typography variant="caption" color="text.disabled" fontWeight={500}>
                    {CARD_LABELS[id]}
                  </Typography>
                  <Chip label="hidden" size="small" sx={{ height: 16, fontSize: "0.6rem", color: "#94A3B8", bgcolor: "#F1F5F9" }} />
                </Stack>
                <Tooltip title="Show card" arrow>
                  <IconButton size="small" onClick={() => onToggleCard(id)} sx={{ color: "#94A3B8", "&:hover": { color: "#059669" } }}>
                    <Visibility sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          );
        }

        return (
          <Grid
            key={id}
            item
            xs={12}
            sm={6}
            lg={Math.max(3, Math.floor(12 / colCount)) as any}
            sx={{ height: CARD_HEIGHT }}
            draggable={editMode}
            onDragStart={editMode ? () => { dragging.current = id; } : undefined}
            onDragOver={editMode ? (e) => { e.preventDefault(); setDragOver(id); } : undefined}
            onDrop={editMode ? () => handleDrop(id) : undefined}
          >
            <Box
              sx={{
                height: "100%",
                position: "relative",
                borderRadius: "10px",
                outline: editMode
                  ? dragOver === id ? "2px dashed #0EA5E9" : "2px dashed #CBD5E1"
                  : "none",
                bgcolor: dragOver === id ? "#EFF6FF" : "transparent",
                transition: "background 0.15s, outline-color 0.15s",
                cursor: editMode ? "grab" : "default",
                "&:active": editMode ? { cursor: "grabbing" } : {},
                p: editMode ? 0.5 : 0,
              }}
            >
              {editMode && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 20,
                    bgcolor: "white",
                    borderRadius: "6px",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    px: 0.75,
                    py: 0.25,
                  }}
                >
                  <Tooltip title="Hide card" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onToggleCard(id)}
                      sx={{ p: 0.25, color: "#94A3B8", "&:hover": { color: "#EF4444" } }}
                    >
                      <VisibilityOff sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <DragIndicator sx={{ fontSize: 16, color: "#CBD5E1" }} />
                </Stack>
              )}
              <Box sx={{ height: "100%" }}>
                {CARD_COMPONENTS[id]}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default RecentSection;
